import { EditorV3Content } from "../classes/EditorV3Content";
import { IEditorV3 } from "../classes/interface";

/**
 * Replaces text in EditorV3 content with new EditorV3 content.
 * Finds the specified phrase and replaces it with the provided EditorV3 content, preserving formatting where appropriate.
 *
 * @param input - The EditorV3 content to perform replacement in
 * @param oldPhrase - The text phrase to find and replace
 * @param newPhrase - The EditorV3 content to insert as replacement
 * @param newLineChar - The character(s) used for line breaks in multi-line replacements (default: "~")
 * @returns The modified IEditorV3 content with replacements applied
 */
export const replaceTextInEditorV3 = (
  input: IEditorV3,
  oldPhrase: string,
  newPhrase: IEditorV3,
  newLineChar = "~",
): IEditorV3 => {
  const ret: EditorV3Content = new EditorV3Content(input);
  // Check for characters in the old phrase
  // Check the phrase exists in the target
  /* istanbul ignore else */
  if (oldPhrase !== "" && ret.getTextPosition(oldPhrase)) {
    // Get replacement content
    const newContent = new EditorV3Content(newPhrase);
    // New content style
    const newContentStyleMap = new Set(
      newContent.lines.flatMap((line) => line.textBlocks.map((block) => block.style)),
    );
    const newContentStyle =
      newContentStyleMap.size === 1 ? newContentStyleMap.values().next().value : undefined;
    // Add line breaks to replacement content
    while (newContent.getTextPosition(newLineChar)?.length ?? 0 > 0) {
      const pos = newContent.getTextPosition(newLineChar);
      /* istanbul ignore else */
      if (pos) newContent.splitLine(pos[0]);
    }
    // Update all lines
    const pos = ret.getTextPosition(oldPhrase);
    /* istanbul ignore else */
    if (pos && pos.length > 0) {
      for (let i = pos.length - 1; i >= 0; i--) {
        const thisPos = pos[i];
        // Check if target has a single style
        const target = ret.subLines(thisPos);
        const targetStyle =
          target.length === 1 && target[0].textBlocks.length === 1
            ? target[0].textBlocks[0].style
            : // istanbul ignore next
              undefined;
        // Create this replacement
        const thisReplacement = new EditorV3Content(newContent);
        // Check new content has no style
        /* istanbul ignore else */
        if (targetStyle && !newContentStyle) {
          thisReplacement.applyStyle(targetStyle, {
            startLine: 0,
            startChar: 0,
            endLine: thisReplacement.lines.length - 1,
            endChar: thisReplacement.lines[thisReplacement.lines.length - 1].lineLength,
          });
        }
        // Update the content
        ret.splice(thisPos, thisReplacement.lines);
      }
    }
  }
  return ret.data;
};
